// Activity line chart shared by WAF, Bot, and API Security Events.
// Uses heatmap APIs unchanged; frontend clips the series to the page time range.

import React from "react";
import Chart from "react-apexcharts";
import axios from "../../../../utils/axios/v1/userAxios";
import { Box, Stack, Typography, Skeleton, IconButton, Tooltip, CircularProgress } from "@mui/material";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import FlameIcon from "@mui/icons-material/LocalFireDepartmentOutlined";
import { ChevronLeft, ChevronRight, Info } from "lucide-react";

import colors from "../../../../utils/colors";
import { formatDate, getBrowserTimeZone } from "../../../../utils/format";
import { withApexChartFont, apexVerticalAreaGradient, apexNoOfYAxisTitle } from "../../../../utils/apexChartDefaults";
import useHome from "../../../../hooks/user/useHome";
import LayerdIcon from "../../../common/LayerdIcon";

const INDIGO = colors.primary[300];

/** Joined prev/next control — matches v2 GhostButton border/radius. */
const HEATMAP_WEEK_NAV_GROUP_SX = {
  display: "inline-flex",
  alignItems: "stretch",
  flexShrink: 0,
  height: 32,
  borderRadius: "8px",
  border: `1px solid ${colors.neutral[200]}`,
  backgroundColor: colors.neutral[50],
  overflow: "hidden",
};

const HEATMAP_WEEK_NAV_BTN_BASE_SX = {
  width: 34,
  height: 32,
  minWidth: 34,
  borderRadius: 0,
  p: 0,
  borderRight: `1px solid ${colors.neutral[200]}`,
  transition: "background-color 120ms ease, color 120ms ease, box-shadow 120ms ease",
  "&:last-of-type": {
    borderRight: "none",
  },
};

const HEATMAP_WEEK_NAV_BTN_ENABLED_SX = {
  color: colors.neutral[1000],
  backgroundColor: colors.neutral[100],
  "&:hover": {
    backgroundColor: colors.primary[100],
    color: colors.primary[300],
  },
  "&:active": {
    backgroundColor: colors.primary.alpha,
  },
};

const HEATMAP_WEEK_NAV_BTN_DISABLED_SX = {
  color: colors.neutral[300],
  backgroundColor: colors.neutral[50],
  opacity: 1,
  cursor: "not-allowed",
  pointerEvents: "auto",
  "&:hover": {
    backgroundColor: colors.neutral[50],
    color: colors.neutral[300],
  },
  "& svg": {
    opacity: 0.45,
  },
};

const ACTIVITY_HELP_TOOLTIP =
  "Event counts for the selected time range. Hover a point for that bucket’s exact time and event count.";

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

const PERIOD_MS = {
  "30m": 30 * 60 * 1000,
  "60m": 60 * 60 * 1000,
  "3h": 3 * HOUR_MS,
  "6h": 6 * HOUR_MS,
  "24h": DAY_MS,
  "1d": DAY_MS,
  "3d": 3 * DAY_MS,
  "7d": 7 * DAY_MS,
  "1M": 30 * DAY_MS,
};

const TOOLTIP_SX = {
  maxWidth: 360,
  fontSize: 12,
  lineHeight: "18px",
  color: "#fff",
  backgroundColor: "#1F2937",
  "& .MuiTooltip-arrow": { color: "#1F2937" },
  p: 1.5,
};

// Build a 7×24 dense matrix (row=day 0..6 oldest→today, col=hour) from
// the sparse cells the API returns. Day-offset comes from CH as
// "days-since-now", so today = 0; we flip to row order 0=oldest...6=today.
function _matrix(cells) {
  const m = Array.from({ length: 7 }, () => new Array(24).fill(0));
  for (const c of cells || []) {
    const dayOffset = Number(c.day_offset);
    const hour = Number(c.hour);
    const count = Number(c.count);
    if (!Number.isFinite(dayOffset) || !Number.isFinite(hour)) continue;
    if (hour < 0 || hour > 23) continue;
    const row = Math.max(0, Math.min(6, 6 - dayOffset));
    m[row][hour] = Number.isFinite(count) ? count : 0;
  }
  return m;
}

/** Last calendar day of the selected week (weekOffset 0 = today). */
function _weekEndDate(weekOffset) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - weekOffset * 7);
  return d;
}

/** Row 0 = oldest day in week, row 6 = week end (browser local; matches API time_zone). */
function _localDateForRow(rowIdx, weekOffset = 0) {
  const end = _weekEndDate(weekOffset);
  const d = new Date(end);
  d.setDate(d.getDate() - (6 - rowIdx));
  return d;
}

function _summaryDayName(dayOffset, weekOffset = 0) {
  if (weekOffset === 0 && dayOffset === 0) return "Today";
  if (weekOffset === 0 && dayOffset === 1) return "Yesterday";
  const end = _weekEndDate(weekOffset);
  const d = new Date(end);
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() - dayOffset);
  return new Intl.DateTimeFormat(undefined, { weekday: "short" }).format(d);
}

function _heatmapWindowLabel(weekOffset = 0) {
  const end = _weekEndDate(weekOffset);
  const start = new Date(end);
  start.setDate(start.getDate() - 6);
  const fmt = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });
  return `${fmt.format(start)} – ${fmt.format(end)}`;
}

/** Resolve page time_range (+ optional customDateRange) to [fromMs, toMs]. */
function _resolveActivityBounds(timeRange, customDateRange) {
  const toMs = Date.now();
  if (!timeRange) return { fromMs: toMs - DAY_MS, toMs };

  const fromRaw = timeRange.from ?? timeRange.start;
  const toRaw = timeRange.to ?? timeRange.end;
  if (fromRaw != null && toRaw != null) {
    const fromMs = new Date(fromRaw).getTime();
    const endMs = new Date(toRaw).getTime();
    if (Number.isFinite(fromMs) && Number.isFinite(endMs) && fromMs < endMs) {
      return { fromMs, toMs: endMs };
    }
  }

  const period =
    (typeof timeRange.period === "string" && timeRange.period) ||
    (typeof timeRange === "string" ? timeRange : null);

  if (period === "custom" && Array.isArray(customDateRange) && customDateRange.length === 2) {
    const fromMs = new Date(customDateRange[0]).getTime();
    const endMs = new Date(customDateRange[1]).getTime();
    if (Number.isFinite(fromMs) && Number.isFinite(endMs) && fromMs < endMs) {
      return { fromMs, toMs: endMs };
    }
  }

  const span = PERIOD_MS[period] || PERIOD_MS["7d"];
  return { fromMs: toMs - span, toMs };
}

function _formatBoundsLabel(fromMs, toMs) {
  const span = toMs - fromMs;
  const fmtDay = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });
  const fmtTime = new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  if (span <= DAY_MS + HOUR_MS) {
    return `${fmtTime.format(fromMs)} – ${fmtTime.format(toMs)}`;
  }
  return `${fmtDay.format(fromMs)} – ${fmtDay.format(toMs)}`;
}

/**
 * Flatten 7×24 matrix into chronological [timestamp, count] points.
 * Same Apex datetime format as WAFDetectionInsight (`[x, y]` tuples).
 */
function _toTimeSeries(matrix, weekOffset = 0) {
  const points = [];
  for (let ri = 0; ri < 7; ri++) {
    const day = _localDateForRow(ri, weekOffset);
    for (let hour = 0; hour < 24; hour++) {
      const ts = new Date(day.getTime());
      ts.setHours(hour, 0, 0, 0);
      const t = ts.getTime();
      if (!Number.isFinite(t)) continue;
      points.push([t, Number(matrix[ri][hour]) || 0]);
    }
  }
  return points;
}

/** Keep buckets that overlap [fromMs, toMs). `bucketMs` is series interval size. */
function _clipSeriesToBounds(points, fromMs, toMs, bucketMs = HOUR_MS) {
  if (!Array.isArray(points) || !points.length) return [];
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || fromMs >= toMs) return points;
  const step = Number.isFinite(bucketMs) && bucketMs > 0 ? bucketMs : HOUR_MS;
  return points.filter(([t]) => t + step > fromMs && t < toMs);
}

/** Floor `ms` to activity bucket start in local time (matches CH toStartOfInterval TZ). */
function _alignBucketStartLocal(ms, intervalMs) {
  const step = Number.isFinite(intervalMs) && intervalMs > 0 ? intervalMs : HOUR_MS;
  const d = new Date(ms);
  if (step >= HOUR_MS) {
    d.setMinutes(0, 0, 0);
    const hours = Math.round(step / HOUR_MS);
    if (hours > 1) {
      const h = d.getHours();
      d.setHours(Math.floor(h / hours) * hours, 0, 0, 0);
    }
    return d.getTime();
  }
  const stepMins = Math.max(1, Math.round(step / 60000));
  const totalMins = d.getHours() * 60 + d.getMinutes();
  const aligned = Math.floor(totalMins / stepMins) * stepMins;
  d.setHours(Math.floor(aligned / 60), aligned % 60, 0, 0);
  return d.getTime();
}

function _nextBucketLocal(ms, intervalMs) {
  const step = Number.isFinite(intervalMs) && intervalMs > 0 ? intervalMs : HOUR_MS;
  if (step >= HOUR_MS) {
    const d = new Date(ms);
    d.setHours(d.getHours() + Math.max(1, Math.round(step / HOUR_MS)), 0, 0, 0);
    return d.getTime();
  }
  return ms + step;
}

/**
 * Fill every bucket in [fromMs, toMs) so the line stays continuous at y=0
 * when there is no traffic (API series is sparse / only non-zero groups).
 */
function _densifySeriesWithZeros(points, fromMs, toMs, intervalMs) {
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || fromMs >= toMs) {
    return Array.isArray(points) ? points : [];
  }
  const step = Number.isFinite(intervalMs) && intervalMs > 0 ? intervalMs : HOUR_MS;
  const byT = new Map();
  for (const [t, c] of points || []) {
    if (!Number.isFinite(t)) continue;
    byT.set(t, Number(c) || 0);
  }

  const lookup = (bucketT) => {
    if (byT.has(bucketT)) return byT.get(bucketT);
    // Tolerate tiny CH/local ms skew within half a bucket.
    let best = null;
    let bestDist = Infinity;
    const half = step / 2;
    for (const [k, v] of byT) {
      const dist = Math.abs(k - bucketT);
      if (dist < bestDist && dist < half) {
        bestDist = dist;
        best = v;
      }
    }
    return best != null ? best : 0;
  };

  let t = _alignBucketStartLocal(fromMs, step);
  if (t + step <= fromMs) t = _nextBucketLocal(t, step);
  const last = _alignBucketStartLocal(toMs - 1, step);
  const out = [];
  const maxPts = 20000;
  for (let guard = 0; t <= last && guard < maxPts; guard += 1) {
    out.push([t, lookup(t)]);
    t = _nextBucketLocal(t, step);
  }
  return out;
}

function _escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Tooltip from the real series point (never interpolated curve Y). */
function _activityTooltipHtml(ts, count) {
  const when = _escapeHtml(formatDate(new Date(ts)));
  const n = Number(count);
  if (!Number.isFinite(n)) return "";
  const label = _escapeHtml(`${n.toLocaleString()} event${n === 1 ? "" : "s"}`);
  return (
    `<div class="apexcharts-tooltip-title" style="font-family: inherit; font-size: 12px;">${when}</div>` +
    `<div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex; padding: 4px 8px;">` +
    `<span class="apexcharts-tooltip-marker" style="background-color: rgb(99, 113, 247);"></span>` +
    `<div class="apexcharts-tooltip-text" style="font-family: inherit; font-size: 12px;">` +
    `<div class="apexcharts-tooltip-y-group">` +
    `<span class="apexcharts-tooltip-text-y-label">Events: </span>` +
    `<span class="apexcharts-tooltip-text-y-value">${label}</span>` +
    `</div></div></div>`
  );
}

function _seriesFromApi(series) {
  if (!Array.isArray(series)) return null;
  return series
    .map((h) => [Number(h.t), Number(h.count) || 0])
    .filter(([t]) => Number.isFinite(t) && t > 0);
}

/** Summary stats derived from clipped points (matches visible line). */
function _summaryFromClippedPoints(points) {
  if (!points.length) {
    return {
      hottest: null,
      busiestDayLabel: null,
      busiestDayCount: 0,
      quietest: null,
      periodTotal: 0,
      avgPerDay: 0,
    };
  }

  let hottest = null;
  const byDay = new Map();
  const byHour = new Map();
  let periodTotal = 0;

  for (const [t, count] of points) {
    const n = Number(count) || 0;
    periodTotal += n;
    const d = new Date(t);
    const dayKey = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    byDay.set(dayKey, (byDay.get(dayKey) || 0) + n);
    const hour = d.getHours();
    byHour.set(hour, (byHour.get(hour) || 0) + n);
    if (!hottest || n > hottest.count) {
      hottest = { t, count: n, hour };
    }
  }

  let busiestDayCount = 0;
  let busiestLabel = null;
  for (const [k, n] of byDay) {
    if (n > busiestDayCount) {
      busiestDayCount = n;
      const [y, m, d] = k.split("-").map(Number);
      busiestLabel = new Intl.DateTimeFormat(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
      }).format(new Date(y, m, d));
    }
  }

  let quietest = null;
  for (let start = 0; start < 24; start += 1) {
    let sum = 0;
    for (let i = 0; i < 4; i += 1) sum += byHour.get((start + i) % 24) || 0;
    if (!quietest || sum < quietest.count) quietest = { start_hour: start, count: sum };
  }

  const dayCount = Math.max(1, byDay.size);

  return {
    hottest,
    busiestDayLabel: busiestLabel,
    busiestDayCount,
    quietest,
    periodTotal,
    avgPerDay: periodTotal > 0 ? Math.round((periodTotal / dayCount) * 10) / 10 : 0,
  };
}

function ActivityHeaderHelp() {
  return (
    <Tooltip
      title={ACTIVITY_HELP_TOOLTIP}
      placement="top"
      arrow
      componentsProps={{ tooltip: { sx: TOOLTIP_SX } }}
    >
      <IconButton
        size="small"
        aria-label="About activity chart"
        sx={{
          p: 0.25,
          ml: -0.25,
          color: INDIGO,
          "&:hover": { color: colors.primary[500], backgroundColor: "rgba(99, 113, 247, 0.08)" },
        }}
      >
        <LayerdIcon icon={Info} size={16} color={INDIGO} strokeWidth={2} />
      </IconButton>
    </Tooltip>
  );
}

function HeatmapWeekNavButton({ tip, disabled, onClick, icon: Icon }) {
  const wrap = (child) => (
    <Tooltip title={tip} arrow placement="top">
      <span style={{ display: "flex", lineHeight: 0 }}>{child}</span>
    </Tooltip>
  );

  return wrap(
    <IconButton
      size="small"
      aria-label={tip}
      disabled={disabled}
      onClick={onClick}
      disableRipple={disabled}
      sx={{
        ...HEATMAP_WEEK_NAV_BTN_BASE_SX,
        ...(disabled ? HEATMAP_WEEK_NAV_BTN_DISABLED_SX : HEATMAP_WEEK_NAV_BTN_ENABLED_SX),
        "&.Mui-disabled": HEATMAP_WEEK_NAV_BTN_DISABLED_SX,
      }}
    >
      <LayerdIcon
        icon={Icon}
        size={18}
        color={disabled ? colors.neutral[300] : colors.neutral[1000]}
        strokeWidth={disabled ? 1.75 : 2.25}
      />
    </IconButton>
  );
}

function HeatmapWeekNav({ canGoOlder, canGoNewer, loading, onOlder, onNewer }) {
  const olderDisabled = !canGoOlder || loading;
  const newerDisabled = !canGoNewer || loading;

  return (
    <Box role="group" aria-label="Week navigation" sx={HEATMAP_WEEK_NAV_GROUP_SX}>
      <HeatmapWeekNavButton
        tip={canGoOlder ? "Previous week" : "No earlier weeks in the selected time range"}
        disabled={olderDisabled}
        onClick={onOlder}
        icon={ChevronLeft}
      />
      <HeatmapWeekNavButton
        tip={canGoNewer ? "Next week (toward today)" : "Already viewing the current week"}
        disabled={newerDisabled}
        onClick={onNewer}
        icon={ChevronRight}
      />
    </Box>
  );
}

function HeatmapSummarySkeleton() {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2.5}
      sx={{
        mt: 1.25,
        pt: 1.75,
        borderTop: `1px solid ${colors.neutral[200]}`,
        flexWrap: "wrap",
        rowGap: 1.5,
      }}
    >
      {[0, 1, 2, 3].map((i) => (
        <React.Fragment key={`sh_stat_${i}`}>
          {i > 0 ? <Divider /> : null}
          <Box sx={{ display: "flex", flexDirection: "column", minWidth: 130, flex: "1 1 130px" }}>
            <Skeleton variant="text" width={72} height={12} sx={{ maxWidth: "100%" }} />
            <Skeleton variant="text" width={96} height={22} sx={{ mt: 0.5, maxWidth: "100%" }} />
            <Skeleton variant="text" width={112} height={17} sx={{ mt: 0.25, maxWidth: "100%" }} />
          </Box>
        </React.Fragment>
      ))}
    </Stack>
  );
}

/** Coerce API / list totals to a finite non‑negative number (CH UInt64 may arrive as string). */
function coerceNonNegNumber(value) {
  if (typeof value === "number" && Number.isFinite(value) && value >= 0) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return null;
}

export default function EventsActivityHeatmap({
  endpoint = "log/waf_events_heatmap",
  requestFn,
  mapRequestBody,
  resetKey,
  siteId,
  timeRange,
  conditions,
  customDateRange,
  action,
  /** Exact adaptive series + zero densify — enable only on event listing pages. */
  exactSeries = false,
  /**
   * Optional full-range total from the events list (same filters/time).
   * When set, Period Total prefers this over week-clipped chart sums so
   * dashboard Top-card counts match (LE-399 / LE-432).
   */
  periodTotalOverride = undefined,
}) {
  const { curProjectId } = useHome();
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [weekOffset, setWeekOffset] = React.useState(0);
  const navCapsRef = React.useRef({ has_older: false });

  const browserTz = React.useMemo(() => getBrowserTimeZone(), []);

  const conditionsKey = React.useMemo(
    () => JSON.stringify(conditions || []),
    [conditions]
  );
  const timeRangeKey = React.useMemo(
    () => JSON.stringify(timeRange || {}),
    [timeRange]
  );

  const resetKeyMemo = React.useMemo(
    () => (resetKey != null ? String(resetKey) : ""),
    [resetKey]
  );

  React.useEffect(() => {
    setWeekOffset(0);
    navCapsRef.current = { has_older: false };
  }, [siteId, timeRangeKey, conditionsKey, endpoint, action, resetKeyMemo]);

  React.useEffect(() => {
    if (!timeRange) return undefined;
    const ctrl = new AbortController();
    setLoading(true);
    let body = { time_range: timeRange, time_zone: browserTz, week_offset: weekOffset };
    if (curProjectId) body.project_id = curProjectId;
    if (siteId && siteId !== "all") body.site_id = siteId;
    if (conditions?.length) body.conditions = conditions;
    if (action != null) body.action = action;
    if (mapRequestBody) body = mapRequestBody(body);
    const run = requestFn || ((payload, opts) => axios.post(endpoint, payload, opts));
    run(body, { signal: ctrl.signal })
      .then((r) => {
        // Support both axios `{ data }` and already-unwrapped bodies.
        // Prefer the object that carries cells/series; keep matching_total if nested.
        let payload = r?.data?.cells != null || r?.data?.summary != null || r?.data?.series != null
          ? r.data
          : (r?.cells != null || r?.summary != null || r?.series != null ? r : (r?.data ?? r));
        if (payload && payload.matching_total == null && r?.data?.matching_total != null) {
          payload = { ...payload, matching_total: r.data.matching_total };
        }
        if (payload && payload.matching_total == null && payload.summary?.matching_total != null) {
          payload = { ...payload, matching_total: payload.summary.matching_total };
        }
        setData(payload);
        navCapsRef.current = { has_older: Boolean(payload?.has_older) };
        if (typeof payload?.week_offset === "number" && payload.week_offset !== weekOffset) {
          setWeekOffset(payload.week_offset);
        }
      })
      .catch((e) => { if (e.message !== "canceled" && e.code !== "ERR_CANCELED") setData(null); })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [siteId, curProjectId, timeRangeKey, conditionsKey, timeRange, conditions, endpoint, action, browserTz, weekOffset, requestFn, mapRequestBody, resetKeyMemo]);

  const bounds = React.useMemo(
    () => _resolveActivityBounds(timeRange, customDateRange),
    [timeRange, customDateRange]
  );
  const rangeSpanMs = bounds.toMs - bounds.fromMs;
  const showWeekNav = rangeSpanMs >= 7 * DAY_MS - HOUR_MS;

  const canGoOlder = showWeekNav && Boolean(data?.has_older ?? navCapsRef.current.has_older);
  const canGoNewer = showWeekNav && weekOffset > 0;

  const intervalMs = React.useMemo(() => {
    if (!exactSeries) return HOUR_MS;
    const sec = Number(data?.interval_seconds);
    if (Number.isFinite(sec) && sec > 0) return sec * 1000;
    return HOUR_MS;
  }, [exactSeries, data?.interval_seconds]);

  const fullSeriesPoints = React.useMemo(() => {
    if (exactSeries) {
      const fromApi = _seriesFromApi(data?.series);
      if (fromApi) return fromApi;
    }
    const matrix = _matrix(Array.isArray(data?.cells) ? data.cells : []);
    return _toTimeSeries(matrix, weekOffset);
  }, [exactSeries, data, weekOffset]);

  const clippedPoints = React.useMemo(() => {
    const clipped = _clipSeriesToBounds(fullSeriesPoints, bounds.fromMs, bounds.toMs, intervalMs);
    if (!exactSeries) return clipped;
    // Event pages: densify so quiet gaps render as y=0 instead of a broken line.
    return _densifySeriesWithZeros(clipped, bounds.fromMs, bounds.toMs, intervalMs);
  }, [exactSeries, fullSeriesPoints, bounds.fromMs, bounds.toMs, intervalMs]);

  const clippedSummary = React.useMemo(
    () => _summaryFromClippedPoints(clippedPoints),
    [clippedPoints]
  );

  // Period Total priority (LE-399 / LE-431 / LE-432):
  // 1) list total override (same filters as events table / dashboard card)
  // 2) heatmap matching_total (full time_range)
  // 3) week-clipped chart sum (unfiltered / fallback)
  const matchingTotalNumber = React.useMemo(() => {
    const fromOverride = coerceNonNegNumber(periodTotalOverride);
    if (fromOverride != null) return fromOverride;
    const fromApi =
      coerceNonNegNumber(data?.matching_total) ??
      coerceNonNegNumber(data?.summary?.matching_total);
    return fromApi;
  }, [periodTotalOverride, data?.matching_total, data?.summary?.matching_total]);

  const periodTotalDisplay = React.useMemo(() => {
    if (matchingTotalNumber != null) return matchingTotalNumber;
    return clippedSummary.periodTotal;
  }, [matchingTotalNumber, clippedSummary.periodTotal]);

  const avgPerDayDisplay = React.useMemo(() => {
    if (matchingTotalNumber != null) {
      const dayMs = 24 * 60 * 60 * 1000;
      const days = Math.max(1, Math.round((bounds.toMs - bounds.fromMs) / dayMs));
      return matchingTotalNumber > 0 ? Math.round((matchingTotalNumber / days) * 10) / 10 : 0;
    }
    return clippedSummary.avgPerDay;
  }, [matchingTotalNumber, bounds.fromMs, bounds.toMs, clippedSummary.avgPerDay]);

  const windowLabel = React.useMemo(
    () => _formatBoundsLabel(bounds.fromMs, bounds.toMs),
    [bounds.fromMs, bounds.toMs]
  );
  const bucketLabel = React.useMemo(() => {
    const sec = Number(data?.interval_seconds) || 3600;
    if (sec < 60) return `${sec}s`;
    if (sec < 3600) {
      const m = Math.round(sec / 60);
      return m === 1 ? "1 minute" : `${m} minutes`;
    }
    const h = Math.round(sec / 3600);
    return h === 1 ? "1 hour" : `${h} hours`;
  }, [data?.interval_seconds]);
  const totalLabel = "Period total";
  const chartKey = `${weekOffset}-${bounds.fromMs}-${bounds.toMs}-${periodTotalDisplay}-${clippedPoints.length}`;

  const series = React.useMemo(
    () => [{ name: "Events", data: clippedPoints }],
    [clippedPoints]
  );

  const options = React.useMemo(
    () =>
      withApexChartFont({
        chart: {
          type: "area",
          stacked: false,
          toolbar: { show: false },
          zoom: { enabled: false },
          animations: { enabled: true },
          sparkline: { enabled: false },
          foreColor: "#696969",
        },
        dataLabels: { enabled: false },
        colors: ["rgb(99, 113, 247)"],
        stroke: {
          // Straight segments so hover maps 1:1 to exact bucket times/counts.
          curve: "straight",
          width: 2,
        },
        fill: apexVerticalAreaGradient({
          toColor: "rgba(99, 113, 247, 0.25)",
        }),
        markers: { size: 0, hover: { size: 5 } },
        xaxis: {
          type: "datetime",
          min: bounds.fromMs,
          max: bounds.toMs,
          labels: {
            datetimeUTC: false,
            style: { fontSize: "12px", colors: "#696969" },
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
          tooltip: { enabled: false },
        },
        yaxis: {
          tickAmount: 5,
          min: 0,
          forceNiceScale: true,
          title: apexNoOfYAxisTitle("No. of Events"),
          labels: {
            style: { colors: "#696969", fontSize: "12px" },
            formatter: (v) => (Number.isFinite(v) ? Math.round(v) : v),
          },
        },
        grid: {
          show: true,
          borderColor: "#e6e6e6",
          strokeDashArray: 0,
          padding: { left: 8 },
          xaxis: { lines: { show: false } },
          yaxis: { lines: { show: true } },
        },
        legend: { show: false },
        tooltip: {
          shared: false,
          intersect: false,
          // Prevent sticky/custom tooltip layers from stealing clicks on the page.
          cssClass: "waf-activity-apex-tooltip",
          custom({ series, seriesIndex, dataPointIndex, w }) {
            if (dataPointIndex == null || dataPointIndex < 0) return "";
            const count = series?.[seriesIndex]?.[dataPointIndex];
            const ts = w?.globals?.seriesX?.[seriesIndex]?.[dataPointIndex];
            if (!Number.isFinite(ts)) return "";
            return _activityTooltipHtml(ts, count);
          },
        },
      }),
    [bounds.fromMs, bounds.toMs]
  );

  return (
    <Box sx={{
      backgroundColor: colors.neutral[100],
      border: `1px solid ${colors.neutral[200]}`,
      borderRadius: "12px",
      overflow: "hidden",
    }}>
      {/* Head */}
      <Stack direction="row" alignItems="center" justifyContent="space-between"
        sx={{ p: "14px 18px", borderBottom: `1px solid ${colors.neutral[200]}` }}>
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
          <ShowChartIcon sx={{ fontSize: 16, color: colors.neutral[600], flexShrink: 0 }} />
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: colors.neutral[1000],
              flexShrink: 0,
            }}
          >
            Activity
          </Typography>
          <ActivityHeaderHelp />
          <Stack
            direction="row"
            alignItems="center"
            spacing={3}
            sx={{ flexShrink: 0 }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: 12,
                fontWeight: 400,
                color: colors.neutral[600],
                fontVariantNumeric: "tabular-nums",
                whiteSpace: "nowrap",
                lineHeight: "17px",
                flexShrink: 0,
              }}
            >
              {windowLabel}
            </Typography>
            {showWeekNav ? (
              <HeatmapWeekNav
                canGoOlder={canGoOlder}
                canGoNewer={canGoNewer}
                loading={loading}
                onOlder={() => setWeekOffset((w) => w + 1)}
                onNewer={() => setWeekOffset((w) => Math.max(0, w - 1))}
              />
            ) : null}
          </Stack>
        </Stack>
      </Stack>

      {/* Body — area chart + summary */}
      <Box sx={{ p: "18px 18px 20px" }}>
        {loading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ height: 250 }}>
            <CircularProgress sx={{ color: colors.neutral[400] }} />
          </Stack>
        ) : (
          <Box sx={{
            width: "100%",
            minHeight: 250,
            // Custom Apex tooltips can leave a layer that intercepts clicks.
            "& .waf-activity-apex-tooltip, & .apexcharts-tooltip": {
              pointerEvents: "none !important",
            },
          }}>
            <Chart
              key={chartKey}
              options={options}
              series={series}
              type="area"
              height={250}
              width="100%"
            />
          </Box>
        )}

        {loading ? (
          <HeatmapSummarySkeleton />
        ) : (
          <Stack direction="row" alignItems="center" spacing={2.5}
            sx={{ mt: 1.25, pt: 1.75, borderTop: `1px solid ${colors.neutral[200]}`, flexWrap: "wrap", rowGap: 1.5 }}>
            <SummaryStat
              label={<><FlameIcon sx={{ fontSize: 11, color: INDIGO, verticalAlign: "-1px" }} /> Hottest cell</>}
              value={clippedSummary.hottest
                ? new Intl.DateTimeFormat(undefined, {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  }).format(new Date(clippedSummary.hottest.t))
                : "—"}
              sub={clippedSummary.hottest
                ? `${(clippedSummary.hottest.count || 0).toLocaleString()} events in ${bucketLabel}`
                : "no traffic yet"}
              accent
            />
            <Divider />
            <SummaryStat
              label="Busiest day"
              value={clippedSummary.busiestDayLabel || "—"}
              sub={clippedSummary.busiestDayLabel
                ? `${clippedSummary.busiestDayCount.toLocaleString()} events`
                : ""}
            />
            <Divider />
            <SummaryStat
              label="Quietest window"
              value={clippedSummary.quietest != null
                ? `${String(clippedSummary.quietest.start_hour).padStart(2, "0")}:00 – ${String((clippedSummary.quietest.start_hour + 4) % 24).padStart(2, "0")}:00`
                : "—"}
              sub={clippedSummary.quietest != null
                ? `${clippedSummary.quietest.count} events in range`
                : ""}
            />
            <Divider />
            <SummaryStat
              label={totalLabel}
              value={periodTotalDisplay.toLocaleString()}
              sub={avgPerDayDisplay > 0 ? `avg ${avgPerDayDisplay} / day` : ""}
            />
          </Stack>
        )}
      </Box>
    </Box>
  );
}

function SummaryStat({ label, value, sub, accent }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", minWidth: 130, flex: "1 1 130px" }}>
      <Typography sx={{
fontSize: 10.5, fontWeight: 700,
        letterSpacing: "0.05em", textTransform: "uppercase",
        color: colors.neutral[600], mb: 0.4,
      }}>{label}</Typography>
      <Typography sx={{
        fontSize: 15, fontWeight: 700,
        color: accent ? INDIGO : colors.neutral[1000],
        fontVariantNumeric: "tabular-nums",
        minHeight: 22,
        lineHeight: "22px",
        whiteSpace: "nowrap",
      }}>{value}</Typography>
      <Typography sx={{
        fontSize: 11,
        color: colors.neutral[700],
        mt: 0.25,
        fontVariantNumeric: "tabular-nums",
        minHeight: 17,
        lineHeight: "17px",
        visibility: sub ? "visible" : "hidden",
      }}>
        {sub || "\u00a0"}
      </Typography>
    </Box>
  );
}

function Divider() {
  return <Box sx={{ width: "1px", height: 36, backgroundColor: colors.neutral[200] }} />;
}
